/**
 * BadgePanel — Badges section (redesigned).
 *
 * Two INDEPENDENT sections:
 *   SUPERVISORS (top)  — badges 1–6, permanently assigned to S-01 through S-06.
 *     Pulled from fom_roster. Show supervisor badge image + name + on/off-duty.
 *     Cannot be reassigned from this panel.
 *   OFFICERS (bottom)  — officer badge pool, badges 1–16.
 *
 * Supervisor and officer pools are COMPLETELY INDEPENDENT — same badge numbers,
 * different physical badges. The officer section always shows all badges in the
 * store (1–16) without filtering by supervisor badge IDs.
 */
import { useEffect, useState } from 'react'
import { Card, Button } from '../ui'
import { useDesktopStore } from '../../stores/desktop'
import { markDirty } from '../../stores/sync'
import { SUPERVISOR_BADGE } from '../../assets'
import { loadRoster } from '../personnel/personnelHelpers'
import type { Badge as BadgeType } from '../../stores/desktop'
import OfficerBadgeTile from './OfficerBadgeTile'

// Roster categories that own a supervisor badge slot
const SUP_ROSTER_CATS = new Set([
  'Director of Security', 'Assistant Director', 'Lieutenant', 'Sergeants',
  'Dir/AD/Lt', 'Sergeant', 'OIC', 'Developer',
])

export default function BadgePanel() {
  const {
    badges, setBadges,
    oosIds, setOosIds,
    supOosIds, setSupOosIds,
    activatedBadgeIds, setActivatedBadgeIds,
    personnel,
  } = useDesktopStore()

  const [showOosMenu, setShowOosMenu] = useState(false)
  const [showSupOosMenu, setShowSupOosMenu] = useState(false)
  const [addInput, setAddInput] = useState('')

  // ── Supervisor tiles (from roster) ────────────────────────────────────────
  const roster = loadRoster()
  type SupTile = { callSign: string; name: string; badgeId: number; cat: string }
  const supervisorTiles: SupTile[] = []
  for (const [cat, entries] of Object.entries(roster)) {
    if (!SUP_ROSTER_CATS.has(cat)) continue
    for (const e of entries) {
      if (e.badgeNumber && e.badgeNumber >= 1 && e.badgeNumber <= 6) {
        supervisorTiles.push({ callSign: e.callSign, name: e.name, badgeId: e.badgeNumber, cat })
      }
    }
  }
  supervisorTiles.sort((a, b) => a.badgeId - b.badgeId)

  // ── Officer badges ─────────────────────────────────────────────────────────
  // Officer badges (1–16) are an independent pool — do NOT filter by supervisor
  // badge IDs. Same numbers, completely separate physical badges.
  const officerBadges = badges

  // Supervisor roles — these should ONLY appear in the supervisor section above,
  // never in the officer badge tiles even if they share the same badge number.
  const SUP_ROLES = new Set([
    'Dir/AD/Lt', 'Sergeant', 'OIC', 'Developer',
    'Lieutenant', 'Director of Security', 'Assistant Director', 'Director',
  ])

  // On-duty helpers
  function isSupOnDuty(callSign: string) {
    return personnel.some(p => p.callSign === callSign && p.name)
  }
  function getAssignedOfficer(badgeId: number) {
    // Only match officers — supervisors belong to their own independent badge pool
    return personnel.find(p => p.badge === String(badgeId) && p.name && !SUP_ROLES.has(p.role))
  }

  // Auto-sync onDuty status from personnel (officers only — supervisors have their own section)
  useEffect(() => {
    const updated = badges.map(b => {
      const onDuty = personnel.some(p => p.badge === String(b.id) && p.name && !SUP_ROLES.has(p.role))
      if (onDuty && b.status !== 'onDuty')  return { ...b, status: 'onDuty'      as BadgeType['status'] }
      if (!onDuty && b.status === 'onDuty') return { ...b, status: 'inShadowBox' as BadgeType['status'] }
      return b
    })
    if (updated.some((b, i) => b.status !== badges[i].status)) setBadges(updated)
    // When a badge goes on-duty, automatically clear its OOS flag
    // This fixes the dashboard double-count: badge is no longer OOS once checked in
    const onDutyIds = updated.filter(b => b.status === 'onDuty').map(b => b.id)
    const cleanedOos = oosIds.filter(id => !onDutyIds.includes(id))
    if (cleanedOos.length !== oosIds.length) setOosIds(cleanedOos)
  }, [personnel])

  function setStatus(id: number, status: BadgeType['status']) {
    if (status === 'onDuty') return
    markDirty()
    setBadges(badges.map(b => b.id === id ? { ...b, status } : b))
  }
  function toggleActivated(id: number) {
    markDirty()
    setActivatedBadgeIds(activatedBadgeIds.includes(id)
      ? activatedBadgeIds.filter(x => x !== id)
      : [...activatedBadgeIds, id])
  }
  function toggleOos(id: number) {
    markDirty()
    setOosIds(oosIds.includes(id) ? oosIds.filter(x => x !== id) : [...oosIds, id])
  }
  function toggleSupOos(id: number) {
    markDirty()
    setSupOosIds(supOosIds.includes(id) ? supOosIds.filter(x => x !== id) : [...supOosIds, id])
  }
  function addBadge() {
    const id = parseInt(addInput)
    if (isNaN(id) || id < 1) { alert('Enter a valid badge number'); return }
    if (badges.some(b => b.id === id)) { alert('Badge already exists'); return }
    markDirty()
    setBadges([...badges, { id, status: 'inShadowBox' }])
    setAddInput('')
  }

  const supsOnDuty = supervisorTiles.filter(s => isSupOnDuty(s.callSign)).length

  return (
    <Card variant="item" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid var(--border)',
      }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#1d4ed8' }}>Badges</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input
        type="number" value={addInput} onChange={e => setAddInput(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && addBadge()} placeholder="Badge #"
        style={{ background: 'var(--bg-deep)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', fontSize: 13, padding: '5px 8px', outline: 'none', width: 80 }}
        />
        <Button variant="primary" size="sm" onClick={addBadge}>Add Badge</Button>
        </div>
        {/* Supervisor OOS */}
        <div style={{ position: 'relative' }}>
        <Button variant="ghost" size="md" onClick={() => setShowSupOosMenu(m => !m)}>
        <span>Sup OOS</span>
          <span style={{ fontSize: 11, color: '#6b7280' }}>({supOosIds.length})</span>
        </Button>
        {showSupOosMenu && (
        <div style={{
        position: 'absolute', right: 0, top: '110%', width: 220,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 10px 25px rgba(0,0,0,0.4)', zIndex: 999, maxHeight: 280, overflowY: 'auto',
        }}>
        <div style={{ padding: '8px 12px' }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 4 }}>Supervisor badges OOS</div>
        {supervisorTiles.map(s => (
          <label key={s.callSign} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 13, color: 'var(--text-primary)' }}>
          <input type="checkbox" checked={supOosIds.includes(s.badgeId)} onChange={() => toggleSupOos(s.badgeId)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
        {s.name.split(',')[0]} (Badge {s.badgeId})
        </label>
        ))}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>
          <Button variant="primary" size="sm" onClick={() => setShowSupOosMenu(false)} style={{ width: '100%', justifyContent: 'center' }}>Done</Button>
        </div>
        </div>
        )}
        </div>
        {/* Officer OOS */}
          <div style={{ position: 'relative' }}>
            <Button variant="ghost" size="md" onClick={() => setShowOosMenu(m => !m)}>
              <span>Ofc OOS</span>
              <span style={{ fontSize: 11, color: '#6b7280' }}>({oosIds.length})</span>
            </Button>
            {showOosMenu && (
              <div style={{
                position: 'absolute', right: 0, top: '110%', width: 256,
                background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10,
                boxShadow: '0 10px 25px rgba(0,0,0,0.4)', zIndex: 999, maxHeight: 320, overflowY: 'auto',
              }}>
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 4 }}>
                    Officer badges — mark Out of Service
                  </div>
                  {badges.map(b => (
                    <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 13, color: 'var(--text-primary)' }}>
                      <input type="checkbox" checked={oosIds.includes(b.id)} onChange={() => toggleOos(b.id)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
                      Badge {b.id}
                    </label>
                  ))}
                </div>
                <div style={{ borderTop: '1px solid var(--border)', padding: 8 }}>
                  <Button variant="primary" size="sm" onClick={() => setShowOosMenu(false)} style={{ width: '100%', justifyContent: 'center' }}>Done</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── SUPERVISOR SECTION ── */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
          Supervisors
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: supsOnDuty > 0 ? '#9b59b6' : 'var(--text-muted)' }}>
            {supsOnDuty > 0 ? `${supsOnDuty} on duty` : 'none on duty'}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(supervisorTiles.length, 1)}, 1fr)`, gap: 10 }}>
          {supervisorTiles.map(sup => {
            const onDuty = isSupOnDuty(sup.callSign)
            const isOos  = supOosIds.includes(sup.badgeId)
            return (
              <div key={sup.callSign} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '10px 8px', borderRadius: 8, background: 'var(--bg-elevated)',
                borderLeft: `3px solid ${onDuty ? '#9b59b6' : isOos ? '#ef4444' : 'transparent'}`,
                opacity: isOos ? 0.5 : 1,
                minWidth: 0,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: onDuty ? 'var(--text-primary)' : 'var(--text-muted)',
                  textAlign: 'center', marginBottom: 6, lineHeight: 1.2,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%',
                }}>
                  {sup.name.split(',')[0].trim()}
                </div>
                <div style={{ position: 'relative', width: 72, height: 101, flexShrink: 0 }}>
                  <img
                    src={SUPERVISOR_BADGE} alt={`Badge ${sup.badgeId}`}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', filter: isOos ? 'grayscale(1) brightness(0.5)' : 'none' }}
                  />
                  <div style={{
                    position: 'absolute', top: '47%', left: '50%',
                    transform: 'translate(-50%,-50%)', fontWeight: 900,
                    fontSize: 16, color: isOos ? '#999' : '#1e1b4b',
                    textShadow: '0 0 2px rgba(255,255,255,0.8)', pointerEvents: 'none', lineHeight: 1,
                  }}>
                    {String(sup.badgeId).padStart(2, '0')}
                  </div>
                </div>
                <div style={{ marginTop: 7, fontSize: 11, fontWeight: 600, textAlign: 'center',
                  color: isOos ? '#ef4444' : onDuty ? '#9b59b6' : '#6b7280' }}>
                  {isOos ? 'OOS' : onDuty ? 'On Duty' : 'Off Duty'}
                </div>
              </div>
            )
          })}
          {supervisorTiles.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 8 }}>
              No supervisors with badges 1–6 found in roster
            </div>
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border)', marginBottom: 18 }} />

      {/* ── OFFICER SECTION ── */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
          Officers
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12 }}>
          {officerBadges.map(b => {
            const high      = b.id >= 15
            const activated = !high || activatedBadgeIds.includes(b.id)
            const isOos     = oosIds.includes(b.id)
            const officer   = getAssignedOfficer(b.id)
            return (
              <OfficerBadgeTile
                key={b.id} badge={b} isOos={isOos} isActivated={activated}
                highNumbered={high} officer={officer}
                onSetStatus={status => setStatus(b.id, status)}
                onToggleActivated={() => toggleActivated(b.id)}
              />
            )
          })}
        </div>
      </div>

      {/* ── Footer counts ── */}
      <div style={{
        display: 'flex', gap: 16, marginTop: 12, paddingTop: 10,
        borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)', flexWrap: 'wrap',
      }}>
        <span>Sups On Duty: <strong style={{ color: '#9b59b6' }}>{supsOnDuty}</strong></span>
        <span>Shadow Box: <strong style={{ color: '#3b82f6' }}>{officerBadges.filter(b => !oosIds.includes(b.id) && b.status === 'inShadowBox').length}</strong></span>
        <span>Has Badge: <strong style={{ color: '#a855f7' }}>{officerBadges.filter(b => !oosIds.includes(b.id) && b.status === 'hasPhysicalBadge').length}</strong></span>
        <span>Ofc On Duty: <strong style={{ color: '#22c55e' }}>{officerBadges.filter(b => !oosIds.includes(b.id) && b.status === 'onDuty').length}</strong></span>
        <span>OOS (Ofc): <strong style={{ color: '#ef4444' }}>{oosIds.length}</strong></span>
        <span>OOS (Sup): <strong style={{ color: '#ef4444' }}>{supOosIds.length}</strong></span>
      </div>
    </Card>
  )
}
